import { useState, useEffect, useCallback } from "react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const DIRECTIONS = [
  ["office_to_maintenance", "Office → Maintenance"],
  ["maintenance_to_office", "Maintenance → Office"],
  ["cross_division_bonus", "Cross-Division Bonus"],
  ["support_team", "Support Team tasks (properties)"],
];
const DIR_LABEL = Object.fromEntries(DIRECTIONS);
const DIVISIONS = ["Student", "Conventional", "Support"];

function TaskDialog({ task, onClose, onSaved }) {
  const isEdit = !!task;
  const [f, setF] = useState({
    name: task?.name || "",
    direction: task?.direction || "office_to_maintenance",
    points: task?.points ?? 10,
    supervised_by: task?.supervised_by || "",
    visible_to: task?.visible_to || ["Student", "Conventional", "Support"],
  });
  const [busy, setBusy] = useState(false);

  const toggleDiv = (d) =>
    setF((prev) => ({ ...prev, visible_to: prev.visible_to.includes(d) ? prev.visible_to.filter((x) => x !== d) : [...prev.visible_to, d] }));

  const save = async () => {
    if (!f.name.trim() || !f.supervised_by.trim()) return toast.error("Name and sign-off are required");
    if (f.visible_to.length === 0) return toast.error("Pick at least one division that can see this task");
    setBusy(true);
    try {
      const body = { ...f, points: parseInt(f.points, 10) };
      if (isEdit) await api.put(`/admin/tasks/${task.id}`, body);
      else await api.post("/admin/tasks", body);
      toast.success(isEdit ? "Task updated" : "Task added");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="task-dialog">
        <DialogHeader><DialogTitle className="font-display">{isEdit ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Task name</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Shadow a budget review with Accounting" data-testid="task-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })}>
                <SelectTrigger data-testid="task-direction-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DIRECTIONS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`task-direction-${v}`}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Points</Label>
              <Input type="number" min="1" value={f.points} onChange={(e) => setF({ ...f, points: e.target.value })} data-testid="task-points-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Supervisor sign-off</Label>
            <Input value={f.supervised_by} onChange={(e) => setF({ ...f, supervised_by: e.target.value })} placeholder="e.g. CM, Maint. lead, Accounting lead" data-testid="task-signoff-input" />
          </div>
          <div>
            <Label className="text-xs">Who sees it on their task list</Label>
            <div className="flex gap-2 mt-1">
              {DIVISIONS.map((d) => (
                <button key={d} type="button" onClick={() => toggleDiv(d)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${f.visible_to.includes(d) ? "bg-[#1a1f36] text-white border-[#1a1f36]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  data-testid={`task-visible-${d}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>
          {isEdit && task.submission_count > 0 && (
            <p className="text-xs text-slate-600 bg-[#f8f9fc] border border-slate-200 rounded-md p-2">
              {task.submission_count} submission{task.submission_count === 1 ? "" : "s"} already logged — points already earned are never changed by edits.
            </p>
          )}
          <Button onClick={save} disabled={busy} className="w-full sw-red hover:bg-red-700 text-white" data-testid="task-save-btn">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add task"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTasks() {
  const [tasks, setTasks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => api.get("/admin/tasks").then(({ data }) => setTasks(data)), []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (t) => {
    try {
      await api.put(`/admin/tasks/${t.id}`, { active: !t.active });
      toast.success(t.active ? "Task retired — points already earned are kept" : "Task reactivated");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const grouped = DIRECTIONS.map(([dir, label]) => ({ dir, label, items: tasks.filter((t) => t.direction === dir) }));
  const known = new Set(DIRECTIONS.map(([d]) => d));
  const legacy = tasks.filter((t) => !known.has(t.direction));
  const groups = legacy.length > 0 ? [...grouped, { dir: "legacy", label: "Legacy / retired categories", items: legacy }] : grouped;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display font-semibold text-[#1a1f36]">Task manager</h3>
          <p className="text-sm text-slate-600 max-w-2xl">Add, edit, or retire tasks without touching code. Retiring a task hides it from task lists but keeps every point already earned. Support Team tasks are done by properties; Support floors run the property task lists.</p>
        </div>
        <Button size="sm" className="sw-red hover:bg-red-700 text-white shrink-0" onClick={() => setCreating(true)} data-testid="add-task-btn">
          <Plus className="w-4 h-4 mr-1" /> New task
        </Button>
      </div>

      {groups.map(({ dir, label, items }) => items.length > 0 && (
        <div key={dir} className="mb-6">
          <h4 className="font-display font-semibold text-[#1a1f36] mb-2">{label}</h4>
          <div className="border border-slate-200 rounded-md divide-y">
            {items.map((t) => (
              <div key={t.id} className={`px-4 py-2.5 flex flex-wrap items-center gap-3 ${!t.active ? "opacity-60 bg-slate-50" : ""}`} data-testid={`task-mgr-row-${t.id}`}>
                <div className="flex-1 min-w-52">
                  <div className="font-medium text-sm text-[#1a1f36]">
                    {t.name}
                    {!t.active && <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-slate-200 text-slate-600" data-testid={`task-retired-badge-${t.id}`}>Retired</span>}
                  </div>
                  <div className="text-xs text-slate-500">
                    Sign-off: {t.supervised_by} · seen by {(t.visible_to || []).join(", ")}
                    {t.submission_count > 0 && ` · ${t.submission_count} submission${t.submission_count === 1 ? "" : "s"}`}
                  </div>
                </div>
                <span className="font-display font-bold text-[#c8102e]">{t.points}</span>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(t)} data-testid={`task-edit-btn-${t.id}`}>Edit</Button>
                  <Button size="sm" variant="ghost" className={t.active ? "text-[#c8102e]" : "text-green-700"} onClick={() => toggleActive(t)} data-testid={`task-toggle-btn-${t.id}`}>
                    {t.active ? "Retire" : "Reactivate"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {creating && <TaskDialog onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <TaskDialog task={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
